import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
};

export const getters = {
  getStats: $state => $state,
};

// Create a debounced version of the actual API call function
const fetchMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_TAB_META, meta);
  } catch (error) {
    // ignore
  }
};

const debouncedFetchMetaData = debounce(fetchMetaData, 500, false, 1500);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 5000, false, 10000);
const superLongDebouncedFetchMetaData = debounce(
  fetchMetaData,
  10000,
  false,
  20000
);

// Otimizacao de carga de rede, atras da flag de conta network_load_optimization.
// Os contadores das abas (Minhas, Nao atribuidas, Todas) foram 44,7% dos pedidos que chegaram
// ao servidor da Crisdu em 28/10, das 14:00 as 17:30. Com a flag ligada:
//   - cada faixa espera mais que a original, nunca menos;
//   - aba escondida nao pede, nem na chamada nem quando a espera termina: guarda um pedido e
//     faz um so quando volta a ficar visivel;
//   - um pedido agendado antes de uma lista carregar e descartado, porque a lista ja trouxe os
//     contadores da visao atual (sem isso, a espera maior deixaria a contagem de outro filtro
//     ou pasta sobrescrever a certa).
// Sem a flag, ou sem conta carregada, o caminho e exatamente o de antes.
let pendingHiddenFetch = null;
let metaGeneration = 0;

const fetchOptimizedMetaData = async (commit, params, allCount, generation) => {
  if (generation !== metaGeneration) return;
  if (document.visibilityState === 'hidden') {
    pendingHiddenFetch = { commit, params, allCount, generation };
    return;
  }
  try {
    const response = await ConversationApi.meta(params);
    if (generation !== metaGeneration) return;
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_TAB_META, meta);
  } catch (error) {
    // ignore
  }
};

const optimizedDebouncedFetchMetaData = debounce(
  fetchOptimizedMetaData,
  3000,
  false,
  15000
);
const optimizedLongDebouncedFetchMetaData = debounce(
  fetchOptimizedMetaData,
  10000,
  false,
  30000
);
const optimizedSuperLongDebouncedFetchMetaData = debounce(
  fetchOptimizedMetaData,
  15000,
  false,
  60000
);

const isNetworkLoadOptimizationOn = rootGetters => {
  const accountId = rootGetters?.getCurrentAccountId;
  const isFeatureEnabled = rootGetters?.['accounts/isFeatureEnabledonAccount'];
  return !!(
    accountId &&
    typeof isFeatureEnabled === 'function' &&
    isFeatureEnabled(accountId, FEATURE_FLAGS.NETWORK_LOAD_OPTIMIZATION)
  );
};

const optimizedFetch = (commit, params, allCount, generation) => {
  if (allCount > 5000) {
    optimizedSuperLongDebouncedFetchMetaData(
      commit,
      params,
      allCount,
      generation
    );
  } else if (allCount > 100) {
    optimizedLongDebouncedFetchMetaData(commit, params, allCount, generation);
  } else {
    optimizedDebouncedFetchMetaData(commit, params, allCount, generation);
  }
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !pendingHiddenFetch) return;
    const { commit, params, allCount, generation } = pendingHiddenFetch;
    pendingHiddenFetch = null;
    optimizedFetch(commit, params, allCount, generation);
  });
}

export const actions = {
  get: async ({ commit, state: $state, rootGetters }, params) => {
    if (isNetworkLoadOptimizationOn(rootGetters)) {
      const generation = metaGeneration;
      if (document.visibilityState === 'hidden') {
        pendingHiddenFetch = {
          commit,
          params,
          allCount: $state.allCount,
          generation,
        };
        return;
      }
      optimizedFetch(commit, params, $state.allCount, generation);
      return;
    }
    if ($state.allCount > 5000) {
      superLongDebouncedFetchMetaData(commit, params);
    } else if ($state.allCount > 100) {
      longDebouncedFetchMetaData(commit, params);
    } else {
      debouncedFetchMetaData(commit, params);
    }
  },
  set({ commit }, meta) {
    // A lista carregada traz os contadores da visao atual (so o caminho com a flag le isto).
    metaGeneration += 1;
    pendingHiddenFetch = null;
    commit(types.SET_CONV_TAB_META, meta);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
    } = {}
  ) {
    $state.mineCount = mineCount;
    $state.allCount = allCount;
    $state.unAssignedCount = unAssignedCount;
    $state.updatedOn = new Date();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
